/**
 * Pure domain vocabulary for the supporter → decision maker outreach flow.
 *
 * Lives in `utils/` rather than alongside the components because `shared/hooks` needs it too, and the
 * hooks layer is not allowed to import the components layer. Anything here must stay free of React.
 */

import type { I18n } from '@change-corgi/core/i18n';

/**
 * The five reason cards from the component spec (§Screen 2). `own` ("Something else") is the
 * full-width tile and is the only one that carries no prompt. The cards themselves, and their display
 * order, live with the component that renders them (`components/contactDecisionMaker/reasons.ts`) —
 * only the ids are vocabulary the hooks layer shares.
 */
export type ContactDecisionMakerReasonId = 'affects' | 'care' | 'community' | 'right' | 'own';

/**
 * Whether the draft came from the LLM or from the deterministic template the middle-end falls back
 * to. Surfaced so the fallback rate is measurable — it is not shown to the supporter.
 */
export type ContactDecisionMakerDraftSource = 'llm' | 'fallback';

/**
 * How a send failed, in the terms the review screen has to act on:
 *
 * - `retryable` — transport, or a failure the resolver could not classify. The same body may work.
 * - `rejected` — the message as written is not acceptable (blank, too long). Editing it helps.
 * - `blocked` — nothing about the message is wrong: the session expired, or the petition is gone.
 *   Editing cannot help, so offering an edit-and-retry would loop the supporter forever.
 * - `unconfirmed` — the request was abandoned before an answer came back, so whether the message was
 *   stored is unknown. The mutation carries no idempotency key and `createComment` does not dedupe,
 *   so a retry here would deliver a second message to the decision maker.
 *
 * Here rather than beside either consumer: the hook that classifies the resolver's reason and the
 * stage machine that renders the outcome both need it, and `hooks` may not import `components`.
 */
export type ContactDecisionMakerSendFailure = 'retryable' | 'rejected' | 'blocked' | 'unconfirmed';

/** The compose flow's stages, transcribed from the tech doc's state table (§2). */
export type ContactDecisionMakerStage =
	| 'idle'
	| 'story:pick'
	| 'story:write'
	| 'drafting'
	| 'preview'
	| 'preview:editing'
	| 'sending'
	| 'success'
	| 'share'
	| 'contacted';

/**
 * Stages where none of the flow is on screen. Both a guard and a definition: `open` is only accepted
 * from one of these, and focus restoration keys on leaving them. Shared so the reducer and the flow
 * renderer cannot disagree about what "closed" means.
 */
const CONTACT_DECISION_MAKER_CLOSED_STAGES: readonly ContactDecisionMakerStage[] = ['idle', 'contacted'];

/** Whether any of the flow is on screen — the inverse of being in one of the closed stages. */
export function isContactDecisionMakerFlowOnScreen(stage: ContactDecisionMakerStage): boolean {
	return !CONTACT_DECISION_MAKER_CLOSED_STAGES.includes(stage);
}

/**
 * Stages from which the supporter can reach the share sheet in one tap, so its links are worth having
 * in hand. `sending` is included deliberately: fetching while the message is in flight means the sheet
 * has something to render the moment the success screen's CTA is tapped.
 */
const CONTACT_DECISION_MAKER_SHARE_REACHABLE_STAGES: readonly ContactDecisionMakerStage[] = [
	'sending',
	'success',
	'share',
];

/** Whether the post-send share sheet is a tap away — what gates fetching its links. */
export function isContactDecisionMakerShareReachable(stage: ContactDecisionMakerStage): boolean {
	return CONTACT_DECISION_MAKER_SHARE_REACHABLE_STAGES.includes(stage);
}

/** The i18n package is React-free, so importing its `translate` type keeps this module React-free too. */
type SeedDraftTranslate = I18n['translate'];

/**
 * The salutation-plus-story shape the spec seeds the draft with: `Dear {DM},\n\n{story}\n\n`.
 *
 * Used by two paths that need to look identical to the supporter: "Continue with my own words", which
 * skips drafting entirely, and the client-side fallback when the draft mutation cannot be reached at
 * all. Adds nothing of its own — a template that asserted something on the supporter's behalf would
 * be worse than one that just frames their words.
 *
 * The salutation is translated rather than built here: this text is shown to the supporter and sent
 * to the decision maker, so an English "Dear" on an otherwise localised letter is a user-visible bug.
 * `translate` is passed in to keep this module free of React, per the note at the top of the file.
 *
 * OPEN QUESTION (CHANGE-97532): the trailing newlines are a cursor affordance from when this seeded
 * an editable textarea. Since the 2026-09-01 CTA decision both paths land on the read-only review
 * instead, where a bare salutation and trailing blank lines read as an unfinished draft. What the
 * card should show here is Janice's call — guessing makes a visible copy bug, so the shape is
 * unchanged.
 */
export function seedDraft(translate: SeedDraftTranslate, decisionMakerName: string, story: string): string {
	return translate('corgi.contact_decision_maker.story.own_words_draft', {
		decision_maker_name: decisionMakerName,
		story: story.trim(),
	});
}
